/**
 * Лимиты отправки в модерируемый чат ИИ-матча (см. src/configs/matchChat.js).
 *
 * Модуль чистый: история отправок автора и число его сообщений на проверке
 * приходят аргументами, сервер хранит их сам и сам решает, когда записывать
 * отправку (recordChatSend — после того как сообщение принято в очередь
 * модерации: отклоненное модератором тоже считается, иначе спамер перебирал
 * бы тексты без паузы).
 */

import {
  CHAT_REJECTION_REASONS,
  MODERATED_CHAT_MAX_PENDING,
  MODERATED_CHAT_MAX_PER_MINUTE,
  MODERATED_CHAT_MIN_INTERVAL_MS,
  MODERATED_CHAT_RATE_WINDOW_MS,
} from '../../configs/matchChat';

// Какой именно лимит сработал — для логов сервера и подсказки в интерфейсе
export const CHAT_RATE_LIMIT_KINDS = {
  // Слишком часто: меньше MODERATED_CHAT_MIN_INTERVAL_MS с прошлой отправки
  MIN_INTERVAL: 'min_interval',
  // Слишком много за скользящую минуту
  PER_MINUTE: 'per_minute',
  // Слишком много сообщений на проверке — ждать вердиктов, а не времени
  MAX_PENDING: 'max_pending',
};

// Отметки отправок внутри окна: старше окна — не в счет. Нечисловые отметки
// отбрасываются, чтобы испорченная история не заблокировала чат навсегда
const getRecentSends = (sentAtHistory, now, windowMs) => (
  Array.isArray(sentAtHistory) ? sentAtHistory : []
).filter((sentAt) => Number.isFinite(sentAt) && now - sentAt < windowMs);

/**
 * Можно ли автору отправить сообщение прямо сейчас.
 *
 * Сначала — число сообщений на проверке: этот лимит снимают вердикты
 * модератора, а не время, поэтому retryAfterMs у него null. Из временных
 * лимитов сработавших может быть два сразу — тогда ждать нужно дольше из
 * них: клиент показывает кулдаун по retryAfterMs, и короткий срок обманул
 * бы игрока повторным отказом.
 *
 * Отправка ровно через minIntervalMs после прошлой уже разрешена; отправка
 * ровно windowMs назад уже не в счет.
 *
 * @param {Object} params
 * @param {number[]} params.sentAtHistory - моменты прошлых отправок автора
 * @param {number} params.pendingCount - его сообщений на проверке сейчас
 * @param {number} params.now
 * @returns {{allowed: boolean, reason: string|null, retryAfterMs: number|null, limit: string|null}}
 */
export const checkChatRateLimit = ({
  sentAtHistory = [],
  pendingCount = 0,
  now,
  minIntervalMs = MODERATED_CHAT_MIN_INTERVAL_MS,
  maxPerMinute = MODERATED_CHAT_MAX_PER_MINUTE,
  maxPending = MODERATED_CHAT_MAX_PENDING,
  windowMs = MODERATED_CHAT_RATE_WINDOW_MS,
}) => {
  if (Number.isFinite(pendingCount) && pendingCount >= maxPending) {
    return {
      allowed: false,
      reason: CHAT_REJECTION_REASONS.RATE_LIMITED,
      retryAfterMs: null,
      limit: CHAT_RATE_LIMIT_KINDS.MAX_PENDING,
    };
  }

  const recent = getRecentSends(sentAtHistory, now, windowMs);
  let retryAfterMs = 0;
  let limit = null;

  if (recent.length > 0) {
    const lastSentAt = Math.max(...recent);
    const intervalWaitMs = lastSentAt + minIntervalMs - now;
    if (intervalWaitMs > 0) {
      retryAfterMs = intervalWaitMs;
      limit = CHAT_RATE_LIMIT_KINDS.MIN_INTERVAL;
    }
  }

  if (recent.length >= maxPerMinute) {
    // Место освободится, когда из окна выйдет столько самых старых отправок,
    // чтобы осталось maxPerMinute - 1
    const sorted = [...recent].sort((a, b) => a - b);
    const releasingSentAt = sorted[recent.length - maxPerMinute];
    const windowWaitMs = releasingSentAt + windowMs - now;
    if (windowWaitMs > retryAfterMs) {
      retryAfterMs = windowWaitMs;
      limit = CHAT_RATE_LIMIT_KINDS.PER_MINUTE;
    }
  }

  if (limit) {
    return {allowed: false, reason: CHAT_REJECTION_REASONS.RATE_LIMITED, retryAfterMs, limit};
  }
  return {allowed: true, reason: null, retryAfterMs: null, limit: null};
};

/**
 * История отправок после еще одной отправки в момент now. Старше окна —
 * выбрасывается: история нужна только для лимита за минуту, и без обрезки
 * она росла бы весь матч. Исходный массив не меняется.
 */
export const recordChatSend = (history, now, windowMs = MODERATED_CHAT_RATE_WINDOW_MS) => [
  ...getRecentSends(history, now, windowMs),
  now,
];
